// src/hooks/assistant/useAssistantChat.ts
/**
 * Assistant Chat Hook - Thin Orchestrator
 * 
 * Composes focused hooks for a clean, maintainable chat interface.
 * 
 * Responsibilities:
 * - Orchestrates message state, streaming, hydration, and snapshots
 * - Manages input state and form submission
 * - Error handling and retry logic
 * 
 * This is now ~150 lines instead of ~400, with each concern in its own hook.
 */

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import AssistantService from "@/services/assistant/AssistantService";
import { streamMultiAgent } from "@/services/assistant/streamMultiAgent";
import { 
  useAssistantStore, 
  selectIsStreaming, 
  selectMessages,
  selectPlanningByMessage,
  selectToolEventsByMessage,
} from "@/stores/assistantStore";
import { useConversationHydration } from "./useConversationHydration";
import { useMultiAgentSnapshots } from "./useMultiAgentSnapshots";
import type { ChatMessage } from "@/services/assistant/AssistantService";
import type { MultiAgentStreamState } from "@/types/assistant";

// ─────────────────────────────────────────────────────────────────────────────
// Stable empty defaults (prevent new object creation on each render)
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_ERRORS: Record<string, string> = {};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type AssistantMode = "quick" | "workspace" | "deep" | "sheets";

export interface UseAssistantChatOptions {
  conversationId?: string | undefined;
  authToken?: string | undefined;
  onConversationId?: ((conversationId: string, title?: string) => void) | undefined;
  onStreamingChange?: ((conversationId: string, streaming: boolean) => void) | undefined;
  onConversationsShouldRefresh?: (() => void) | undefined;
  resetNonce?: number | undefined;
  mode?: AssistantMode | undefined;
  /** Serialized context for thesis-aware AI (from serializeContextForAPI) */
  context?: Record<string, unknown> | undefined;
}

export interface AssistantChatState {
  messages: ChatMessage[];
  input: string;
  isLoading: boolean;
  isHydrating: boolean;
  showEmpty: boolean;
  planningByMessage: Record<string, boolean>;
  toolEventsByMessage: Record<string, unknown[]>;
  multiAgentStateByMessage: Record<string, MultiAgentStreamState>;
  errorByUserMessageId: Record<string, string>;
  previousRoleByIndex: Array<ChatMessage["role"] | null>;
}

export interface AssistantChatActions {
  setInput: (value: string) => void;
  sendMessage: (e?: React.FormEvent) => Promise<void>;
  retryAfter: (userMessageId: string) => Promise<void>;
  stopStreaming: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useAssistantChat(options: UseAssistantChatOptions): [AssistantChatState, AssistantChatActions] {
  const {
    conversationId,
    authToken,
    onConversationId,
    onStreamingChange,
    onConversationsShouldRefresh,
    resetNonce,
    mode = "deep",
    context,
  } = options;

  // ─── Local State ───
  const [input, setInput] = useState("");
  const [localConversationId, setLocalConversationId] = useState<string | undefined>(undefined);
  const [errorByUserMessageId, setErrorByUserMessageId] = useState<Record<string, string>>(EMPTY_ERRORS);

  // ─── Derived IDs ───
  const effectiveConversationId = conversationId ?? localConversationId;

  // ─── Service Ref ───
  const serviceRef = useRef<AssistantService | null>(null);
  if (!serviceRef.current) {
    serviceRef.current = new AssistantService();
  }

  // ─── Focused Hooks ───
  const { isHydrating: hydrationLoading } = useConversationHydration({
    conversationId,
    authToken,
  });

  const { multiAgentStateByMessage } = useMultiAgentSnapshots(effectiveConversationId);

  // ─── Store Selectors (memoized to prevent re-subscription) ───
  const messagesSelector = useMemo(
    () => selectMessages(effectiveConversationId),
    [effectiveConversationId]
  );
  const streamingSelector = useMemo(
    () => selectIsStreaming(effectiveConversationId),
    [effectiveConversationId]
  );
  const planningSelector = useMemo(
    () => selectPlanningByMessage(effectiveConversationId),
    [effectiveConversationId]
  );
  const toolEventsSelector = useMemo(
    () => selectToolEventsByMessage(effectiveConversationId),
    [effectiveConversationId]
  );

  // ─── Subscribe to store with stable selectors ───
  const messages = useAssistantStore(messagesSelector);
  const isLoading = useAssistantStore(streamingSelector);
  const planningByMessage = useAssistantStore(planningSelector);
  const toolEventsByMessage = useAssistantStore(toolEventsSelector);

  // ─── Store Actions ───
  const setBaseline = useAssistantStore((s) => s.setBaseline);
  const stopStreamAction = useAssistantStore((s) => s.stopStream);
  const setPreserveOnUnmount = useAssistantStore((s) => s.setPreserveOnUnmount);

  // ─── Derived State ───
  const showEmpty = !conversationId && messages.length === 0;
  const isHydrating = conversationId ? (hydrationLoading && messages.length === 0) : false;

  const previousRoleByIndex = useMemo(() => {
    const prev: Array<ChatMessage["role"] | null> = [];
    for (let i = 0; i < messages.length; i++) {
      prev[i] = i > 0 ? (messages[i - 1]?.role ?? null) : null;
    }
    return prev;
  }, [messages]);

  // ─── Effects ───

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setPreserveOnUnmount(false);
    };
  }, [setPreserveOnUnmount]);

  // Clear local ID when parent provides real ID
  useEffect(() => {
    if (conversationId) {
      setLocalConversationId(undefined);
    }
  }, [conversationId]);

  // Handle reset signal
  useEffect(() => {
    if (resetNonce === undefined) return;
    setLocalConversationId(undefined);
    setErrorByUserMessageId(EMPTY_ERRORS);
  }, [resetNonce]);

  // Clear errors when assistant responds
  useEffect(() => {
    if (!messages.length) return;
    setErrorByUserMessageId((prev) => {
      if (prev === EMPTY_ERRORS) return prev; // No errors to clear
      const next = { ...prev };
      let changed = false;
      for (const userId of Object.keys(next)) {
        const uIdx = messages.findIndex((m) => m.id === userId);
        if (uIdx === -1) continue;
        const hasAssistantAfter = messages.slice(uIdx + 1).some((m) => m.role === "assistant");
        if (hasAssistantAfter) {
          delete next[userId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [messages]);

  // ─── Actions ───

  const streamFromHistory = useCallback(
    async (
      targetConversationId: string,
      history: ChatMessage[],
      sourceUserMessageId: string,
      seedTitle: string | undefined,
      shouldSeedOnStart: boolean
    ) => {
      onStreamingChange?.(targetConversationId, true);
      setErrorByUserMessageId((prev) => {
        if (!prev[sourceUserMessageId]) return prev;
        const next = { ...prev };
        delete next[sourceUserMessageId];
        return next;
      });

      try {
        await streamMultiAgent(serviceRef.current!, targetConversationId, history, {
          onStart: (id) => {
            if (shouldSeedOnStart) {
              const title = (seedTitle || "").slice(0, 80) || "Conversation";
              onConversationId?.(id, title);
            }
            onStreamingChange?.(id, true);
          },
          onEnd: (id) => {
            onStreamingChange?.(id, false);
            onConversationsShouldRefresh?.();
          },
          ...((mode === "quick" || mode === "workspace") && { agent: "quick_chat" as const }),
          ...(authToken && { authToken }),
          ...(context && { context }),
        });
      } catch (err) {
        if (!(err instanceof DOMException && err.name === "AbortError")) {
          const msg = err instanceof Error ? err.message : "Unknown error";
          setErrorByUserMessageId((prev) => ({ ...prev, [sourceUserMessageId]: msg }));
        }
      }
    },
    [onConversationId, onStreamingChange, onConversationsShouldRefresh, mode, authToken, context]
  );

  const sendMessage = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      const content = input.trim();
      if (!content || isLoading) return;

      const newUserMessage: ChatMessage = { id: crypto.randomUUID(), role: "user", content };
      const cid = conversationId ?? localConversationId ?? crypto.randomUUID();
      const isNewConversation = !conversationId;
      
      if (!conversationId && !localConversationId) {
        setLocalConversationId(cid);
      }
      
      // Optimistically update store
      setBaseline(cid, [...messages, newUserMessage]);
      setInput("");
      
      // Pass the conversation ID explicitly to avoid stale closure issues
      await streamFromHistory(cid, [...messages, newUserMessage], newUserMessage.id, content, isNewConversation);
    },
    [input, isLoading, messages, conversationId, localConversationId, streamFromHistory, setBaseline]
  );

  const retryAfter = useCallback(
    async (userMessageId: string) => {
      if (isLoading) return;
      const idx = messages.findIndex((m) => m.id === userMessageId);
      if (idx === -1) return;
      
      const cid = conversationId ?? localConversationId;
      if (!cid) return; // Can't retry without a conversation
      
      const history = messages.slice(0, idx + 1);
      await streamFromHistory(cid, history, userMessageId, history[idx]?.content, false);
    },
    [isLoading, messages, conversationId, localConversationId, streamFromHistory]
  );

  const stopStreaming = useCallback(() => {
    const cid = conversationId ?? localConversationId;
    if (cid) {
      stopStreamAction(cid);
      onStreamingChange?.(cid, false);
    }
  }, [conversationId, localConversationId, stopStreamAction, onStreamingChange]);

  // ─── Return ───

  const state: AssistantChatState = {
    messages,
    input,
    isLoading,
    isHydrating,
    showEmpty,
    planningByMessage,
    toolEventsByMessage,
    multiAgentStateByMessage,
    errorByUserMessageId,
    previousRoleByIndex,
  };

  const actions: AssistantChatActions = {
    setInput,
    sendMessage,
    retryAfter,
    stopStreaming,
  };

  return [state, actions];
}

export default useAssistantChat;
